/**
 * Service de validation des données d'authentification
 * Vérifie l'intégrité et le format des données reçues du partenaire
 */

import { logger } from '../../utils/debug/logger.js';

/**
 * Valide le format d'un email
 * @param {string} email - Email à valider
 * @returns {boolean} true si valide
 */
function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;

    // Regex basique pour validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Valide le format d'un ID utilisateur
 * @param {string} userId - ID utilisateur à valider
 * @returns {boolean} true si valide
 */
function isValidUserId(userId) {
    if (!userId || typeof userId !== 'string') return false;

    // Doit contenir au moins 3 caractères et pas d'espaces
    return userId.length >= 3 && !/\s/.test(userId);
}

/**
 * Valide le format d'un ID partenaire
 * @param {string} partnerId - ID partenaire à valider
 * @returns {boolean} true si valide
 */
function isValidPartnerId(partnerId) {
    if (!partnerId || typeof partnerId !== 'string') return false;

    // Doit contenir au moins 3 caractères
    return partnerId.length >= 3;
}

/**
 * Valide le format d'un token
 * @param {string} token - Token à valider
 * @returns {boolean} true si valide
 */
function isValidToken(token) {
    if (!token || typeof token !== 'string') return false;

    // Doit contenir au moins 10 caractères et pas d'espaces
    return token.length >= 10 && !/\s/.test(token);
}

/**
 * Détecte les caractères suspects ou encodage corrompu
 * @param {string} str - Chaîne à vérifier
 * @returns {boolean} true si des caractères suspects sont détectés
 */
function hasSuspiciousCharacters(str) {
    if (!str || typeof str !== 'string') return false;

    // Détecte les caractères de contrôle (sauf newline/tab), NULL bytes, etc.
    // eslint-disable-next-line no-control-regex
    const suspiciousRegex = /[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/;
    return suspiciousRegex.test(str);
}

/**
 * Valide les données d'authentification utilisateur
 * @param {Object} userData - Données utilisateur à valider
 * @param {string} userData.id - ID utilisateur
 * @param {string} userData.email - Email utilisateur
 * @returns {Object} Résultat de validation {isValid, errors}
 */
export function validateUserData(userData) {
    const errors = [];

    if (!userData || typeof userData !== 'object') {
        errors.push('userData must be an object');
        return { isValid: false, errors };
    }

    // Validation de l'ID
    if (!isValidUserId(userData.id)) {
        errors.push(`Invalid user ID format: "${userData.id}"`);
    }

    // Validation de l'email
    if (!isValidEmail(userData.email)) {
        errors.push(`Invalid email format: "${userData.email}"`);
    }

    // Détection de caractères suspects
    if (userData.id && hasSuspiciousCharacters(userData.id)) {
        errors.push('User ID contains suspicious characters (possible corruption)');
    }

    if (userData.email && hasSuspiciousCharacters(userData.email)) {
        errors.push('Email contains suspicious characters (possible corruption)');
    }

    // Validation des champs optionnels s'ils sont présents
    if (userData.firstName && typeof userData.firstName !== 'string') {
        errors.push('firstName must be a string');
    }

    if (userData.lastName && typeof userData.lastName !== 'string') {
        errors.push('lastName must be a string');
    }

    const isValid = errors.length === 0;

    if (!isValid) {
        logger.auth.warn('⚠️ User data validation failed:', errors);
    }

    return { isValid, errors };
}

/**
 * Valide les données d'authentification partenaire
 * @param {Object} partnerData - Données partenaire à valider
 * @param {string} partnerData.id - ID partenaire
 * @param {string} partnerData.token - Token partenaire
 * @returns {Object} Résultat de validation {isValid, errors}
 */
export function validatePartnerData(partnerData) {
    const errors = [];

    if (!partnerData || typeof partnerData !== 'object') {
        errors.push('partnerData must be an object');
        return { isValid: false, errors };
    }

    // Validation de l'ID partenaire
    if (!isValidPartnerId(partnerData.id)) {
        errors.push(`Invalid partner ID format: "${partnerData.id}"`);
    }

    // Validation du token
    if (!isValidToken(partnerData.token)) {
        errors.push('Invalid partner token format (too short or contains spaces)');
    }

    // Détection de caractères suspects
    if (partnerData.id && hasSuspiciousCharacters(partnerData.id)) {
        errors.push('Partner ID contains suspicious characters (possible corruption)');
    }

    if (partnerData.token && hasSuspiciousCharacters(partnerData.token)) {
        errors.push('Partner token contains suspicious characters (possible corruption)');
    }

    const isValid = errors.length === 0;

    if (!isValid) {
        logger.auth.warn('⚠️ Partner data validation failed:', errors);
    }

    return { isValid, errors };
}

/**
 * Valide les données complètes d'authentification (partenaire + utilisateur)
 * @param {Object} authData - Données complètes à valider
 * @returns {Object} Résultat de validation {isValid, errors}
 */
export function validateAuthData(authData) {
    const allErrors = [];

    // Valider les données partenaire
    if (authData.partnerId || authData.partnerToken) {
        const partnerValidation = validatePartnerData({
            id: authData.partnerId,
            token: authData.partnerToken
        });

        if (!partnerValidation.isValid) {
            allErrors.push(...partnerValidation.errors);
        }
    }

    // Valider les données utilisateur
    if (authData.userId || authData.email) {
        const userValidation = validateUserData({
            id: authData.userId,
            email: authData.email,
            firstName: authData.firstName,
            lastName: authData.lastName
        });

        if (!userValidation.isValid) {
            allErrors.push(...userValidation.errors);
        }
    }

    return {
        isValid: allErrors.length === 0,
        errors: allErrors
    };
}
